import { useRouter } from "next/navigation";
import { Box, Container, IconButton, Stack, Typography } from "@mui/material";
import {
  LinkedIn as LinkedInIcon,
  Twitter as TwitterIcon,
  South as DownArrowIcon,
  NorthEastRounded as GetStartedIcon
} from "@mui/icons-material";

import { useStore } from "../../../service/mobx";
import { Section } from "..";
import ButtonOutlined from "../../../component/Button/Outlined";
import CodeBlocks from "./CodeBlocks";
import Pricing from "./Pricing";
import { DiscordIcon } from "../../../component/Icon/Discord";

export default function API({ data }) {
  return (
    <>
      <Section
        display="flex"
        flexDirection="column"
        minHeight="calc(100vh - 80px - 124px)"
        justifyContent="space-around"
        bgcolor="dark.surface.container.lowest"
      >
        <Container disableGutters maxWidth="large">
          <Stack spacing={10} my="auto">
            <Stack
              useFlexGap
              spacing={3}
              direction="row"
              justifyContent="space-around"
              flexWrap={{ compact: "wrap", expanded: "nowrap" }}
            >
              <Typography
                textAlign="center"
                color="dark.surface.on.color"
                typography={{ compact: "displaySm", expanded: "displayLg" }}
              >
                Large AI models,{" "}
                <Box
                  component="span"
                  color="dark.primary.color"
                  fontWeight={200}
                >
                  serverless prices
                </Box>
              </Typography>
              <Stack spacing={2}>
                <Typography
                  variant=""
                  color="dark.surface.on.color"
                  sx={{
                    textWrap: "balance",
                    textAlign: { compact: "center", expanded: "center" },
                    typography: { compact: "titleLg", expanded: "headlineMd" }
                  }}
                >
                  Our API allows developers to run AI models affordably
                </Typography>
                <Stack
                  useFlexGap
                  spacing={2}
                  direction="row"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Outlined
                    text="Learn more"
                    IconEnd={() => (
                      <DownArrowIcon sx={{ ml: 1.5, height: 24, width: 24 }} />
                    )}
                    onClick={() =>
                      document
                        .getElementById("code")
                        .scrollIntoView({ behavior: "smooth", block: "start" })
                    }
                  />
                  <GetStarted />
                </Stack>
              </Stack>
            </Stack>
          </Stack>
        </Container>
      </Section>
      <Hero />
      <CodeBlocks modelList={data.list} />
      <Pricing prices={data.prices} />
      <JoinCommunity />
    </>
  );
}
const Hero = () => (
  <Section minHeight="unset" py={2} bgcolor="dark.surface.color">
    <Container maxWidth="expanded">
      <Typography
        paragraph
        align="center"
        color="dark.secondary.color"
        sx={{ textWrap: "balance" }}
        typography={{ compact: "titleLg", expanded: "displaySm" }}
      >
        <Box component="span" color="dark.primary.color">
          Run inference on GPUs,
        </Box>{" "}
        <Box component="span" color="dark.tertiary.color">
          pay CPU pricing
        </Box>
      </Typography>

      {[
        <>
          Bytez Serverless{" "}
          <Box component="span" color="dark.primary.color" fontWeight={300}>
            (gpu)
          </Box>
        </>,
        <>
          {" "}
          Amazon Lambda{" "}
          <Box component="span" color="dark.error.color" fontWeight={300}>
            (cpu)
          </Box>
        </>
      ].map((component, index) => (
        <Stack
          key={index}
          flexWrap="wrap"
          direction="row"
          justifyContent="space-around"
          spacing={1}
          py={1}
        >
          <Typography
            textAlign="right"
            color="dark.surface.on.color"
            typography={{ compact: "headlineSm", expanded: "headlineLg" }}
          >
            {component}
          </Typography>
          <Typography
            color="dark.surface.on.color"
            sx={{
              typography: { compact: "headlineSm", expanded: "headlineLg" },
              fontWeight: "100 !important"
            }}
          >
            $0.0000166667 / gb sec
          </Typography>
        </Stack>
      ))}
    </Container>
  </Section>
);
export const Outlined = ({ text, onClick, IconEnd }) => (
  <Box>
    <ButtonOutlined
      label={text}
      onClick={onClick}
      IconEnd={IconEnd}
      sx={{
        height: "unset",
        px: 1,
        pl: 2,
        py: 1,
        // pl: 3.5,
        typography: {
          compact: "titleMd"
        },
        textWrap: "balance",
        borderRadius: theme => theme.shape.md.round,
        color: "dark.surface.on.color",
        "&:hover": {
          cursor: "pointer",
          color: "dark.secondary.on.container",
          bgcolor: "dark.secondary.container"
        }
      }}
    />
  </Box>
);
export function GetStarted() {
  const { user } = useStore();
  const route = useRouter();

  return (
    <Outlined
      text="Get started"
      onClick={async () => {
        await user.sign.in.google();
        route.push("/");
      }}
      IconEnd={() => <GetStartedIcon sx={{ ml: 1.5, height: 24, width: 24 }} />}
    />
  );
}

function JoinCommunity() {
  const { user } = useStore();

  return (
    <Box bgcolor="dark.surface.container.color">
      <Container maxWidth="expanded">
        <Stack
          p={3}
          justifyContent="space-around"
          height={256}
          color="dark.surface.on.color"
        >
          <Typography
            align="center"
            color="dark.secondary.color"
            typography={{ compact: "headlineLg", expanded: "displayMd" }}
          >
            Join us
          </Typography>
          <Stack
            flexWrap="wrap"
            direction="row"
            alignItems="center"
            justifyContent="center"
          >
            <IconButton
              sx={{ color: "dark.surface.on.color" }}
              onClick={user.sign.in.discord}
            >
              <DiscordIcon sx={{ height: 64, width: 64 }} />
            </IconButton>
            <IconButton
              sx={{ color: "dark.surface.on.color" }}
              target="_blank"
              href="https://twitter.com/bytez"
            >
              <TwitterIcon sx={{ height: 64, width: 64 }} />
            </IconButton>
            <IconButton
              sx={{ color: "dark.surface.on.color" }}
              target="_blank"
              href="https://www.linkedin.com/company/bytez"
            >
              <LinkedInIcon sx={{ height: 64, width: 64 }} />
            </IconButton>
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
}
